// Curated preset library — 20 pre-designed section variants across the most-
// used block kinds. Each preset returns a fresh section object (with a
// runtime-generated `id`) via `build()`. These live client-side so the
// "Templates" modal opens instantly with no network call.
//
// Adding a new preset? Follow the shape:
//   { id, kind, category, name, vibe, thumb, build: () => ({ id, kind, ...data }) }
// Categories are UI groupings only and can be any short string.

const rid = () => Math.random().toString(36).slice(2, 8);

export const TEMPLATE_CATEGORIES = ["Hero", "Pricing", "FAQ", "Testimonials", "CTA", "Features"];

export const TEMPLATE_PRESETS = [
  // ================= HERO =================
  {
    id: "hero-clean-launch",
    kind: "hero",
    category: "Hero",
    name: "Clean Launch",
    vibe: "Minimal · SaaS · trust-first",
    thumb: "linear-gradient(135deg,#E6EEFF 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "hero",
      title: "Ship your idea in a weekend.",
      subtitle: "The fastest way to turn your expertise into a paid product — no code, no team.",
      cta: "Start free",
      image: "",
    }),
  },
  {
    id: "hero-course-bold",
    kind: "hero",
    category: "Hero",
    name: "Course · Bold",
    vibe: "Coaching · confident · India",
    thumb: "linear-gradient(135deg,#FFEDD5 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "hero",
      title: "Learn from India's #1 finance coach.",
      subtitle: "Live workshops, real portfolios, weekly Q&A. Join 12,000+ learners already investing smarter.",
      cta: "Join the workshop",
      image: "",
    }),
  },
  {
    id: "hero-newsletter",
    kind: "hero",
    category: "Hero",
    name: "Newsletter · Warm",
    vibe: "Editorial · warm · personal",
    thumb: "linear-gradient(135deg,#FEF3C7 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "hero",
      title: "One idea, every Sunday.",
      subtitle: "Short, honest, actionable. A 5-minute read that will change how you think about growth.",
      cta: "Subscribe free",
      image: "",
    }),
  },
  {
    id: "hero-agency-serious",
    kind: "hero",
    category: "Hero",
    name: "Agency · Serious",
    vibe: "Studio · premium · dark accent",
    thumb: "linear-gradient(135deg,#0B0D17 0%, #1F2937 60%)",
    build: () => ({
      id: rid(),
      kind: "hero",
      title: "Design that moves the metric that matters.",
      subtitle: "We partner with founders to design & ship products people love. 40+ launches. 4.9★ average.",
      cta: "Book a consult",
      image: "",
    }),
  },

  // ================= PRICING =================
  {
    id: "pricing-3-tier",
    kind: "pricing",
    category: "Pricing",
    name: "3 Tier · Popular middle",
    vibe: "SaaS classic · featured middle",
    thumb: "linear-gradient(135deg,#F0FDF4 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "pricing",
      title: "Simple pricing. Pay for what you use.",
      plans: [
        { name: "Starter", price: "₹0", perks: ["1 project", "Community support", "Basic analytics"] },
        { name: "Pro", price: "₹1,499/mo", perks: ["10 projects", "Priority support", "Advanced analytics", "Team seats: 5"], featured: true },
        { name: "Business", price: "₹4,999/mo", perks: ["Unlimited projects", "Dedicated CSM", "Custom SSO", "Team seats: unlimited"] },
      ],
    }),
  },
  {
    id: "pricing-workshop",
    kind: "pricing",
    category: "Pricing",
    name: "Workshop · Early bird",
    vibe: "Live event · urgency",
    thumb: "linear-gradient(135deg,#FEE2E2 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "pricing",
      title: "Workshop pricing",
      plans: [
        { name: "Early bird", price: "₹1,999", perks: ["Full 3-day workshop", "Workbook + slides", "Recording access · 30 days"], featured: true },
        { name: "Regular", price: "₹3,999", perks: ["Full 3-day workshop", "Workbook + slides", "Recording access · 30 days"] },
      ],
    }),
  },
  {
    id: "pricing-consulting",
    kind: "pricing",
    category: "Pricing",
    name: "Consulting · Retainer",
    vibe: "Agency · retainer bands",
    thumb: "linear-gradient(135deg,#EDE9FE 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "pricing",
      title: "How we work with you",
      plans: [
        { name: "Sprint", price: "₹1.2L / 2 weeks", perks: ["Discovery + design", "1 team, 1 goal", "Async Slack channel"] },
        { name: "Retainer", price: "₹3.5L / month", perks: ["Design + code + PM", "2 sprints/month", "Weekly review", "Priority SLAs"], featured: true },
        { name: "Enterprise", price: "Custom", perks: ["Dedicated squad", "Roadmap ownership", "Legal + compliance"] },
      ],
    }),
  },

  // ================= FAQ =================
  {
    id: "faq-course",
    kind: "faq",
    category: "FAQ",
    name: "Course FAQ",
    vibe: "5 essentials · course",
    thumb: "linear-gradient(135deg,#DBEAFE 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "faq",
      title: "Frequently asked questions",
      items: [
        { q: "Kya main beginner ke liye theek hoon?", a: "Bilkul. Har lesson zero se shuru hota hai — pre-requisites nahi hain. Aap consistent ho, bas itna kaafi hai." },
        { q: "Kya recording milegi?", a: "Haan — har live class ki recording 30 din tak available rehti hai." },
        { q: "Refund policy?", a: "First 3 days ke andar full refund. Uske baad partial pro-rata." },
        { q: "Kya mujhe koi certificate milega?", a: "Ha, workshop complete karne ke baad verifiable digital certificate milta hai." },
        { q: "Mujhe cost effective option chahiye?", a: "Early bird 50% off milta hai launch ke pehle 48 hours mein." },
      ],
    }),
  },
  {
    id: "faq-agency",
    kind: "faq",
    category: "FAQ",
    name: "Agency FAQ",
    vibe: "Client-facing agency",
    thumb: "linear-gradient(135deg,#F3E8FF 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "faq",
      title: "Everything you might ask",
      items: [
        { q: "How do we get started?", a: "A 30-min discovery call. If we're a fit, we send a 1-page proposal within 24 hours." },
        { q: "Do you sign an NDA?", a: "Yes — mutual NDA is standard. Just send yours or use ours." },
        { q: "Who owns the IP?", a: "You do. Full rights transfer on final invoice payment." },
        { q: "Do you work on retainers?", a: "Yes, minimum 3-month retainers for design + product teams." },
      ],
    }),
  },

  // ================= TESTIMONIALS =================
  {
    id: "test-social-proof",
    kind: "testimonials",
    category: "Testimonials",
    name: "Social proof · 4 quotes",
    vibe: "Trust wall · balanced",
    thumb: "linear-gradient(135deg,#FEF3C7 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "testimonials",
      items: [
        { q: "Best decision I've made this year. Paid for itself in 2 weeks.", n: "Riya Sharma", r: "Founder · Cloud Labs" },
        { q: "The clarity from week 1 alone was worth the whole workshop.", n: "Arjun Mehta", r: "PM · Zomato" },
        { q: "Beautifully paced, no fluff. Rare in this space.", n: "Priya Singh", r: "Content Creator" },
        { q: "I finally understand my own numbers. Life-changing.", n: "Karan Patel", r: "Solo consultant" },
      ],
    }),
  },
  {
    id: "test-launch",
    kind: "testimonials",
    category: "Testimonials",
    name: "Launch reactions · 2",
    vibe: "Product Hunt style",
    thumb: "linear-gradient(135deg,#DBEAFE 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "testimonials",
      items: [
        { q: "The onboarding is buttery smooth. Impressed.", n: "Neha Kapoor", r: "Design lead" },
        { q: "I set up in 4 minutes flat. This is what tooling should feel like.", n: "Aditya Rao", r: "Indie hacker" },
      ],
    }),
  },

  // ================= CTA =================
  {
    id: "cta-newsletter",
    kind: "cta",
    category: "CTA",
    name: "Newsletter opt-in",
    vibe: "Soft ask · list building",
    thumb: "linear-gradient(135deg,#FEF3C7 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "cta",
      title: "Get one useful idea, every Sunday.",
      subtitle: "5-minute read. Unsubscribe anytime.",
      button: "Subscribe free",
    }),
  },
  {
    id: "cta-book-call",
    kind: "cta",
    category: "CTA",
    name: "Book a call",
    vibe: "Consulting · high-intent",
    thumb: "linear-gradient(135deg,#DCFCE7 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "cta",
      title: "Ready to talk?",
      subtitle: "A 30-min call, zero pressure. We'll tell you exactly if we can help.",
      button: "Book a discovery call",
    }),
  },
  {
    id: "cta-workshop-urgency",
    kind: "cta",
    category: "CTA",
    name: "Workshop urgency",
    vibe: "Bold · limited seats",
    thumb: "linear-gradient(135deg,#FEE2E2 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "cta",
      title: "Only 12 seats left. Doors close Sunday.",
      subtitle: "Miss this and the next cohort is 3 months away.",
      button: "Reserve my spot",
    }),
  },

  // ================= FEATURES =================
  {
    id: "features-3-benefit",
    kind: "features",
    category: "Features",
    name: "3 core benefits",
    vibe: "Trust · icon grid",
    thumb: "linear-gradient(135deg,#EDE9FE 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "features",
      title: "Why teams choose us",
      items: [
        { icon: "Zap", t: "Fast by default", d: "Ships in seconds, not sprints. No config, no build steps." },
        { icon: "Shield", t: "Enterprise-grade security", d: "SOC 2 Type II, encrypted at rest and in transit. Your data stays yours." },
        { icon: "Users", t: "Made for teams", d: "Real-time collaboration built in. Comments, roles, and audit logs, free." },
      ],
    }),
  },
  {
    id: "features-6-grid",
    kind: "features",
    category: "Features",
    name: "6 feature grid",
    vibe: "Detailed · SaaS home",
    thumb: "linear-gradient(135deg,#DBEAFE 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "features",
      title: "Everything you need. Nothing you don't.",
      items: [
        { icon: "Zap", t: "Instant setup", d: "Live in under 2 minutes. No credit card required." },
        { icon: "Shield", t: "Encrypted", d: "AES-256 at rest, TLS 1.3 in flight." },
        { icon: "Users", t: "Team roles", d: "Owner · admin · editor · viewer with granular perms." },
        { icon: "BarChart", t: "Analytics", d: "Visits, conversions, funnel drop-offs — in one glance." },
        { icon: "Globe", t: "Custom domain", d: "Bring your own domain with automatic SSL." },
        { icon: "Sparkles", t: "AI copilot", d: "Draft copy, generate images, rewrite tone — all built in." },
      ],
    }),
  },
  {
    id: "features-workshop",
    kind: "features",
    category: "Features",
    name: "Workshop outcomes",
    vibe: "Course · outcomes list",
    thumb: "linear-gradient(135deg,#FEF3C7 0%, #FFFFFF 60%)",
    build: () => ({
      id: rid(),
      kind: "features",
      title: "By the end of this workshop, you will…",
      items: [
        { icon: "Target", t: "Have a written plan", d: "A 90-day roadmap with milestones, KPIs and weekly check-ins." },
        { icon: "TrendingUp", t: "Understand your funnel", d: "Every leaky bucket identified, prioritised, and assigned an owner." },
        { icon: "Zap", t: "Know your first 3 moves", d: "Pre-scripted actions you'll take on day 1, day 7, and day 30." },
      ],
    }),
  },

  // Multi-section templates: a mini "starter stack" (hero+features+cta+faq)
  {
    id: "stack-workshop",
    kind: "__multi__",
    category: "Hero",
    name: "🚀 Workshop starter (4 sections)",
    vibe: "Hero + outcomes + CTA + FAQ",
    thumb: "linear-gradient(135deg,#0B5CFF 0%, #6D28D9 60%)",
    build: () => [
      { id: rid(), kind: "hero", title: "Learn to price your services like the top 1%.", subtitle: "A 3-day live workshop for consultants who charge less than they should.", cta: "Reserve my seat", image: "" },
      { id: rid(), kind: "features", title: "By the end you'll…", items: [
        { icon: "Target", t: "Pricing framework", d: "A repeatable model to quote confidently." },
        { icon: "TrendingUp", t: "Objection scripts", d: "Handle every price pushback like a senior." },
        { icon: "Zap", t: "Invoice templates", d: "Send-ready packs to close faster." },
      ]},
      { id: rid(), kind: "cta", title: "Only 20 seats. Doors close Friday.", subtitle: "Miss this cohort and the next one is in 8 weeks.", button: "Book my seat" },
      { id: rid(), kind: "faq", title: "FAQ", items: [
        { q: "Is this recorded?", a: "Yes — 30-day access to replays." },
        { q: "Refund?", a: "100% refund within 48 hours of purchase." },
        { q: "Team pricing?", a: "3+ seats get 20% off. Email us." },
      ]},
    ],
  },
];
